<script setup lang="ts">
import { Spinner } from "@/components/ui/spinner";
import { BellRingIcon } from "lucide-vue-next";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import WordmarkLogo from "./WordmarkLogo.vue";
import { isNativePlatform } from "../capacitor/platform";
import {
  notificationPermission,
  requestNotificationPermission,
  openNotificationSettings,
} from "../capacitor/notification-permissions";

const { t } = useI18n();

const busy = ref(false);

const visible = computed(() => {
  if (!isNativePlatform()) return false;
  const status = notificationPermission.value;
  return status === "prompt" || status === "denied";
});

const deniedPermanently = computed(() => notificationPermission.value === "denied");

async function handleAction() {
  if (busy.value) return;
  busy.value = true;
  try {
    if (deniedPermanently.value) {
      await openNotificationSettings();
    } else {
      await requestNotificationPermission();
    }
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Transition name="notif-perm-fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-100 flex min-h-full flex-1 flex-col justify-center bg-background pt-24 sm:px-6 lg:px-8"
    >
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <WordmarkLogo class="mx-auto dark:opacity-50" />
      </div>

      <div class="mt-10 flex flex-col items-center gap-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <div class="flex flex-col items-center gap-4 px-4">
          <div
            class="flex h-14 w-14 items-center justify-center rounded-full border border-warning-fill/40 bg-warning-fill/15"
          >
            <BellRingIcon class="h-7 w-7 text-warning" />
          </div>

          <h1 class="text-xl font-semibold text-foreground">
            {{ t("msg.notificationsRequired") }}
          </h1>

          <p class="text-center text-sm text-muted-foreground">
            {{ t("msg.notificationsRequiredDescription") }}
          </p>

          <p v-if="deniedPermanently" class="text-center text-sm text-warning">
            {{ t("msg.notificationsDeniedHint") }}
          </p>
        </div>

        <div class="w-full space-y-4 px-4">
          <button
            @click="handleAction"
            :disabled="busy"
            class="flex w-full items-center justify-center rounded-md bg-amber-500 px-3 py-2.5 text-sm font-semibold text-gray-950 shadow-xs transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Spinner v-if="busy" class="mr-2" />
            {{ deniedPermanently ? t("msg.openNotificationSettings") : t("msg.allowNotifications") }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.notif-perm-fade-enter-active,
.notif-perm-fade-leave-active {
  transition: opacity 0.3s ease;
}
.notif-perm-fade-enter-from,
.notif-perm-fade-leave-to {
  opacity: 0;
}
</style>
